import React, { ChangeEvent, Component } from "react";

import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IRosTopic } from "@formant/core/model/IRosTopic";
import {
    rosTypeBool,
    rosTypeJointState,
    rosTypeLaserScan,
    rosTypePointCloud2,
    rosTypePoseStamped,
} from "@formant/core/model/rosTypes";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";

import {
    EditTeleopStreamPopup,
    IEditTeleopStreamPopupProps,
} from "./EditTeleopStreamPopup";

export interface ITeleopJointStateConfigurationProps extends IEditTeleopStreamPopupProps {
    streamName?: string;
    document: IDeviceConfigurationDocument;
    topics: IRosTopic[];
    onChange: (document: IDeviceConfigurationDocument) => void;
}

export class TeleopJointStateConfiguration extends Component<ITeleopJointStateConfigurationProps> {
    public render() {
        const { streamName, document, topics, onClose, visible } = this.props;
        const stream = document.teleop?.rosStreams?.find(
            _ => _.topicName === streamName,
        );

        const jointStateTopics = topics
            .filter(({ type }) => type === rosTypeJointState)
            .map(_ => _.name);
        const poseTopics = topics
            .filter(_ => _.type === rosTypePoseStamped)
            .map(_ => _.name);
        const boolTopics = topics
            .filter(({ type }) => type === rosTypeBool)
            .map(_ => _.name);
        return (
            <EditTeleopStreamPopup
                title={`Configuration for: ${streamName}`}
                onClose={onClose}
                visible={visible}
            >
                <p>
                    Add an optional JointState topic to which Formant will
                    subscribe and visualize a published motion plan.
                </p>
                <TextField
                    value={stream?.plannedTopic || ""}
                    onChange={this.onChangePlanned}
                    label="Topic name"
                    placeholder="Topic name - eg. /planned/joints"
                    list={jointStateTopics}
                    autoFocus={true}
                    small={true}
                />
                <p>
                    Add an optional PoseStamped topic to which Formant will
                    publish the pose of the end effector.
                </p>
                <TextField
                    value={stream?.endEffectorTopic || ""}
                    onChange={this.onChangePose}
                    label="Topic name"
                    placeholder="Topic name - eg. /planning/pose"
                    list={poseTopics}
                    small={true}
                />
                <p>Link in URDF that represents the end effector</p>
                <TextField
                    value={stream?.endEffectorLinkName || ""}
                    onChange={this.onChangeLink}
                    label="Link name"
                    placeholder="Link name - eg. link_6"
                    small={true}
                />
                <p>
                    Add an optional Boolean topic to which Formant will
                    subscribe and represent if the plan is valid
                </p>
                <TextField
                    value={stream?.planValidTopic || ""}
                    onChange={this.onChangePlanValidTopic}
                    label="Topic name"
                    placeholder="Topic name - eg. /plan/valid"
                    list={boolTopics}
                    autoFocus={true}
                    small={true}
                />
                <p>World Reference Frame</p>
                <TextField
                    value={stream?.baseReferenceFrame || ""}
                    onChange={this.onChangeBaseReferenceFrame}
                    label="Frame id"
                    placeholder="Frame id - eg. base_link"
                    small={true}
                />
                <p>Link in URDF that represents base frame</p>
                <TextField
                    value={stream?.localFrame || ""}
                    onChange={this.onChangeLocalFrame}
                    label="Frame id"
                    placeholder="Topic name - eg. link_1"
                    small={true}
                />
            </EditTeleopStreamPopup>
        );
    }

    private onChangePlanned = (event: ChangeEvent<InputElement>) => {
        const { streamName, document, onChange } = this.props;

        const existing = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === rosTypeJointState &&
                _.mode === "observe",
        );

        if (!existing) {
            return;
        }

        existing.plannedTopic = event.target.value;
        onChange({ ...document });
    };
    private onChangePose = (event: ChangeEvent<InputElement>) => {
        const { streamName, document, onChange } = this.props;

        const existing = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === rosTypeJointState &&
                _.mode === "observe",
        );

        if (!existing) {
            return;
        }

        existing.endEffectorTopic = event.target.value;
        onChange({ ...document });
    };
    private onChangeLink = (event: ChangeEvent<InputElement>) => {
        const { streamName, document, onChange } = this.props;

        const existing = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === rosTypeJointState &&
                _.mode === "observe",
        );

        if (!existing) {
            return;
        }

        existing.endEffectorLinkName = event.target.value;
        onChange({ ...document });
    };
    private onChangePlanValidTopic = (event: ChangeEvent<InputElement>) => {
        const { streamName, document, onChange } = this.props;

        const existing = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === rosTypeJointState &&
                _.mode === "observe",
        );

        if (!existing) {
            return;
        }

        existing.planValidTopic = event.target.value;
        onChange({ ...document });
    };

    private onChangeBaseReferenceFrame = (event: ChangeEvent<InputElement>) => {
        const { streamName, document, onChange } = this.props;

        const existing = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === rosTypeJointState &&
                _.mode === "observe",
        );

        if (!existing) {
            return;
        }

        existing.baseReferenceFrame = event.target.value;

        const existingPointCloudStream = document?.teleop?.rosStreams?.find(
            _ =>
                (_.topicType === rosTypePointCloud2 ||
                    _.topicType === rosTypeLaserScan) &&
                _.mode === "observe",
        );
        if (existingPointCloudStream) {
            existingPointCloudStream.baseReferenceFrame = event.target.value;
        }
        onChange({ ...document });
    };

    private onChangeLocalFrame = (event: ChangeEvent<InputElement>) => {
        const { streamName, document, onChange } = this.props;

        const existing = document.teleop?.rosStreams?.find(
            _ =>
                _.topicName === streamName &&
                _.topicType === rosTypeJointState &&
                _.mode === "observe",
        );

        if (!existing) {
            return;
        }

        existing.localFrame = event.target.value;
        onChange({ ...document });
    };
}
